import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import withAuth from '../../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import { Slide } from 'react-toastify';
import { FiRefreshCcw, FiDownloadCloud, FiX, FiFileText, FiFile, FiEdit2, FiTrash2, FiCalendar, FiUser, FiSearch, FiChevronDown, FiChevronUp, FiClock, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { GET_SALES_REPORT, GET_INVOICE_DETAILS, BASE_URL, CANCEL_SALES_ENTRY, POST_APPROVAL_ENTRY } from '../../utlilities/config';
import axios from 'axios';
import { initializeWebUserInfo, getWebUserInfo } from '../../utlilities/webConfig';
import { TabContext } from '../../components/TabbedLayout';
import { ReportEmbedContext } from '../reportEmbedContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// Import and apply autoTable plugin
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';
import HighlightMetricsModal from './components/HighlightMetricsModal';


// Enhanced Spinner Component
const Spinner = () => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-3">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
        <p className="text-indigo-600 font-medium">Loading...</p>
      </div>
    </div>
);

const RestrictedBlur = ({ restricted, children, className = '' }) => (
    <div className={`relative ${className}`}>
        <div className={restricted ? 'pointer-events-none select-none blur-sm opacity-60' : ''}>
            {children}
        </div>
        {restricted && (
            <div
                className="absolute inset-0 z-10 cursor-not-allowed rounded-lg bg-slate-100/20"
                title="You don't have permission to view pharmacy report amounts"
            />
        )}
    </div>
);

// Helper function to format date in local timezone (IST)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatInvoiceForPrint = (invoiceNo, sequence) => {
    const rawString = String(invoiceNo ?? '').trim();
    if (!rawString) return '';
    // If backend already returned "4/24-25" style, don't change it.
    if (rawString.includes('/')) return rawString;

    const parsedInvoice = Number.parseInt(rawString, 10);
    const invoicePart = Number.isNaN(parsedInvoice)
        ? rawString
        : String(parsedInvoice).padStart(2, '0');

    const finacYear = String(localStorage.getItem('finac_year') || '').trim(); // FY start year (e.g. "2024")
    const storedSeriesName = String(localStorage.getItem('series_name') || '').trim(); // e.g. "24-25"

    const seqYear = Number.parseInt(sequence, 10); // FY end year (e.g. 2025 -> "24-25")
    if (Number.isNaN(seqYear)) {
        return storedSeriesName ? `${invoicePart}/${storedSeriesName}` : invoicePart;
    }

    // Derived series format should match localStorage.series_name convention:
    // Example: Sequence 2026 => "26-27", Sequence 2025 => "25-26"
    const startYY = String(seqYear).slice(-2);
    const endYY = String(seqYear + 1).slice(-2);
    const derivedSeriesName = `${startYY}-${endYY}`;

    // Use the current localStorage series label when bill Sequence matches the current FY start year.
    // localStorage.finac_year is stored as the FY "start year" (e.g. 2026), while series_name becomes "26-27".
    const shouldUseStoredSeries = finacYear && String(seqYear) === String(finacYear);
    const seriesNameToUse = shouldUseStoredSeries ? storedSeriesName : derivedSeriesName;

    return seriesNameToUse ? `${invoicePart}/${seriesNameToUse}` : invoicePart;
};

// Build initial filters
const buildInitialFilters = (filters = []) => {
  const today = new Date();
  const todayFormatted = formatLocalDate(today);

    return filters.reduce((accumulator, filter) => {
        if (filter.type === 'date') {
            // Set both fromDate and toDate to today by default
            const dateValue = todayFormatted;
            return { ...accumulator, [filter.id]: dateValue };
        }

        if (filter.type === 'select') {
            const defaultValue = filter.options?.[0]?.value ?? '';
            return { ...accumulator, [filter.id]: defaultValue };
        }

        if (filter.type === 'search') {
            return { ...accumulator, [filter.id]: '', searchMode: 'patientInvoice' };
        }

        return { ...accumulator, [filter.id]: '' };
    }, { searchMode: 'patientInvoice' });
};

// Report configuration
const reportConfig = {
  filters: [
    { id: 'fromDate', type: 'date', label: 'From Date' },
    { id: 'toDate', type: 'date', label: 'To Date' },
    {
      id: 'paymentStatus',
      type: 'select',
      label: 'Payment Status',
      options: [
        { value: 'all', label: 'All Bills' },
        { value: 'pending', label: 'Pending Bills' },
        { value: 'completed', label: 'Completed Bills' },
      ],
    },
    { id: 'search', type: 'search', label: 'Search' },
  ],
  table: {
    columns: [
      { key: 'InvoiceNo', label: 'Inv. No' },
      { key: 'EntryDate', label: 'Date' },
      { key: 'PatientName', label: 'Patient Name' },
      { key: 'DoctorName', label: 'Doctor Name' },
      { key: 'GrandTotal', label: 'G Total' },
      { key: 'TaxAmount', label: 'Tax Amount' },
      { key: 'DiscountAmount', label: 'Discount' },
      { key: 'TotalAmount', label: 'Total Amount' },
      { key: 'BalanceDue', label: 'Balance Due' },
      { key: 'actions', label: 'Actions' },
    ],
  },
};

// Export Format Selection Modal
const ExportFormatModal = ({ isOpen, onClose, onSelectFormat }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-96 max-w-md transform transition-transform duration-300 ease-out">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center rounded-t-2xl">
                    <h2 className="text-xl font-semibold text-white flex items-center">
                        <FiDownloadCloud className="mr-2" /> Export Report
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
                        aria-label="Close modal"
                    >
                        <FiX size={20} />
                    </button>
                </div>
                <div className="p-6">
                    <p className="text-sm text-slate-600 mb-4">Choose export format:</p>
                    <div className="flex flex-col gap-3">
                        <button
                            onClick={() => onSelectFormat('excel')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                                <FiFileText className="text-green-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to Excel</p>
                                <p className="text-xs text-slate-500">Download as .xlsx file</p>
                            </div>
                        </button>
                        <button
                            onClick={() => onSelectFormat('pdf')}
                            className="flex items-center gap-3 p-4 rounded-lg border-2 border-slate-200 hover:border-indigo-500 hover:bg-indigo-50 transition-all duration-200 group"
                        >
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center group-hover:bg-red-200 transition-colors">
                                <FiFile className="text-red-600" size={20} />
                            </div>
                            <div className="flex-1 text-left">
                                <p className="font-semibold text-slate-800">Export to PDF</p>
                                <p className="text-xs text-slate-500">Download as .pdf file</p>
                            </div>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const SalesBillWise = () => {
    const tabContext = useContext(TabContext);
    const embedded = useContext(ReportEmbedContext);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState(() => buildInitialFilters(reportConfig.filters));
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [salesData, setSalesData] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    const [invoiceDetails, setInvoiceDetails] = useState({});
    const [expandedBills, setExpandedBills] = useState(new Set());
    const [loadingDetails, setLoadingDetails] = useState(new Set());
    const token = localStorage.getItem('accessToken');
    const [userInfo, setUserInfo] = useState({});
    const [showExportModal, setShowExportModal] = useState(false);
    const [permissions, setPermissions] = useState({});
    const [permissionsReady, setPermissionsReady] = useState(false);
    const [generalSettings, setGeneralSettings] = useState({});
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(1000);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });


    const isElectron = () => {
        return window && window.process && window.process.type;
    };
    
    let ipcRenderer;
    
    if (isElectron()) {
        ipcRenderer = window.require('electron').ipcRenderer;
    }


    useEffect(() => {
        const fetchData = async () => {
          let userRolesData;
          let userinfo;
          let generalSetting;

          if (isElectron()) {
            userRolesData = await ipcRenderer.invoke('store-get', 'user_role');
            userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
            generalSetting = await ipcRenderer.invoke('store-get', 'generalopsettings');
            console.log(generalSetting);
            setUserInfo(userinfo);
          } else {
            initializeWebUserInfo();
            userRolesData = getWebUserInfo().user_role; // Fetch from webConfig.js
            generalSetting = getWebUserInfo().generalOpSettings;

            console.log(userinfo);
    
            userinfo = getWebUserInfo().userInfo; // Fetch from webConfig
            setUserInfo(userinfo);
          }
          setPermissions(processPermissions(userRolesData));
          setGeneralSettings(generalSetting);
          setPermissionsReady(true);
        };
        fetchData();
    }, []);


    const processPermissions = (roles) => {
        const permissionsMap = {};
        if (!Array.isArray(roles)) return permissionsMap;
        roles.forEach((role) => {
          (role?.modules || []).forEach((module) => {
            permissionsMap[module.moduleName] = module.permissions;
          });
        });
        return permissionsMap;
    };
    
    const hasPermission = (moduleName, action) => {
        return permissions[moduleName]?.[action] || false;
    };

    const canShowPhReports =
        permissionsReady &&
        (userInfo?.Designation === 'Admin' || hasPermission('ShowPhReports', 'Create'));

    const handleFilterChange = (filterId, value) => {
        setFilters((prev) => ({
            ...prev,
            [filterId]: value,
        }));
    };

    const handleRunReport = () => {
        getSalesReport();
    };

    const handleExport = () => {
        if (!canShowPhReports) return;
        if (salesData.length === 0) {
            toast.error('No data to export', { autoClose: 2000 });
            return;
        }
        setShowExportModal(true);
    };

    const exportToExcel = () => {
        try {
            // Prepare data for Excel
            const excelData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;

                return {
                    'Invoice No': formatInvoiceForPrint(row.InvoiceNo, row.Sequence),
                    'Date': new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    'Patient Name': row.PatientName || '',
                    'Doctor Name': row.DoctorName || '-',
                    'Grand Total': Number(grandTotal.toFixed(2)),
                    'Tax Amount': Number(parseFloat(row.TaxAmount || 0).toFixed(2)),
                    'Discount': Number(parseFloat(row.DiscountAmount || 0).toFixed(2)),
                    'Total Amount': Number(totalAmount.toFixed(2)),
                    'Balance Due': Number(parseFloat(row.BalanceDue || 0).toFixed(2)),
                };
            });

            // Create workbook and worksheet
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(excelData);

            // Set column widths
            const colWidths = [
                { wch: 12 }, // Invoice No
                { wch: 12 }, // Date
                { wch: 25 }, // Patient Name
                { wch: 20 }, // Doctor Name
                { wch: 15 }, // Grand Total
                { wch: 12 }, // Tax Amount
                { wch: 12 }, // Discount
                { wch: 15 }, // Total Amount
                { wch: 15 }, // Balance Due
            ];
            ws['!cols'] = colWidths;

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(wb, ws, 'Sales Bill Wise');

            // Generate filename with date range
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB').replace(/\//g, '-') : 'All';
            const filename = `Sales_Bill_Wise_${fromDate}_to_${toDate}.xlsx`;

            // Write file
            XLSX.writeFile(wb, filename);
            toast.success('Excel file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export to Excel', { autoClose: 2000 });
        }
    };

    const exportToPDF = () => {
        try {
            const doc = new jsPDF('landscape', 'mm', 'a4');
            
            // Add title
            doc.setFontSize(16);
            doc.text('Sales Bill Wise Report', 14, 15);
            
            // Add date range
            doc.setFontSize(10);
            const fromDate = filters.fromDate ? new Date(filters.fromDate).toLocaleDateString('en-GB') : 'All';
            const toDate = filters.toDate ? new Date(filters.toDate).toLocaleDateString('en-GB') : 'All';
            doc.text(`Date Range: ${fromDate} to ${toDate}`, 14, 22);
            
            // Prepare table data
            const tableData = salesData.map((row) => {
                const totalAmount = parseFloat(row.TotalAmount || 0);
                const discountAmount = parseFloat(row.DiscountAmount || 0);
                const grandTotal = totalAmount + discountAmount;

                return [
                    formatInvoiceForPrint(row.InvoiceNo, row.Sequence),
                    new Date(row.EntryDate).toLocaleDateString('en-GB'),
                    row.PatientName || '',
                    row.DoctorName || '-',
                    `${grandTotal.toFixed(2)}`,
                    `${parseFloat(row.TaxAmount || 0).toFixed(2)}`,
                    `${parseFloat(row.DiscountAmount || 0).toFixed(2)}`,
                    `${totalAmount.toFixed(2)}`,
                    `${parseFloat(row.BalanceDue || 0).toFixed(2)}`,
                ];
            });

            // Add table using autoTable function
            autoTable(doc, {
                head: [['Inv. No', 'Date', 'Patient Name', 'Doctor Name', 'G Total', 'Tax Amount', 'Discount', 'Total Amount', 'Balance Due']],
                body: tableData,
                startY: 28,
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [249, 250, 251] },
                margin: { top: 28, left: 14, right: 14 },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 40 },
                    3: { cellWidth: 35 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 25 },
                    6: { cellWidth: 25 },
                    7: { cellWidth: 25 },
                    8: { cellWidth: 25 },
                },
            });

            // Generate filename
            const filename = `Sales_Bill_Wise_${fromDate.replace(/\//g, '-')}_to_${toDate.replace(/\//g, '-')}.pdf`;
            
            // Save PDF
            doc.save(filename);
            toast.success('PDF file exported successfully', { autoClose: 2000 });
            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            toast.error('Failed to export to PDF', { autoClose: 2000 });
        }
    };

    const handleExportFormat = (format) => {
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };

    const getSalesReport = useCallback(async () => {
        setLoading(true);
        try {
            // Convert date strings to Date objects and format as YYYY-MM-DD
            const fromDate = filters.fromDate ? new Date(filters.fromDate) : null;
            const toDate = filters.toDate ? new Date(filters.toDate) : null;
            
            // Format date as YYYY-MM-DD
            const formatDate = (date) => {
                if (!date) return '';
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };
            
            const response = await axios.get(`${BASE_URL}${GET_SALES_REPORT}`, {
                headers: {
                    'XApiKey': process.env.REACT_APP_API_KEY,
                    'Authorization': `Bearer ${token}`,
                },
                params: {
                    'rpType': 'bill',
                    'fromDate': formatDate(fromDate),
                    'toDate': formatDate(toDate),
                    'searchKey': 0,
                    'patientId': 0,
                    'itemNo': 0,
                },
            });
            if (response.status === 200) {
                const rawData = response.data.data || [];
                const userwiseOnly =
                    String(generalSettings?.UserwiseReportonly || '').trim().toLowerCase() === 'yes';
                const isAdmin = userInfo?.Designation === 'Admin';
                const data = userwiseOnly && !isAdmin
                    ? rawData.filter(
                        (bill) =>
                            (bill.UserName || '').trim().toLowerCase() ===
                            (userInfo?.UserName || '').trim().toLowerCase()
                    )
                    : rawData;
                // Sort by InvoiceNo in descending order
                const sortedData = data.sort((a, b) => {
                    const invoiceA = a.InvoiceNo || '';
                    const invoiceB = b.InvoiceNo || '';
                    // Convert to number if possible, otherwise compare as strings
                    const numA = parseInt(invoiceA) || 0;
                    const numB = parseInt(invoiceB) || 0;
                    if (numA !== 0 || numB !== 0) {
                        return numB - numA; // Descending order
                    }
                    return invoiceB.localeCompare(invoiceA); // Descending order for strings
                });
                setSalesData(sortedData);
                setLastRefreshedAt(new Date());
                // Clear expanded bills and details when new data is loaded
                setExpandedBills(new Set());
                setInvoiceDetails({});
                setLoadingDetails(new Set());
                // Reset to first page when new data is loaded
                setCurrentPage(1);
            } else {
                toast.error('Failed to fetch sales report', { autoClose: 1000 });
            }
        } catch (error) {
            console.error('Failed to fetch sales report:', error);
            toast.error('Failed to fetch sales report', { autoClose: 1000 });
        }
        finally {
            setLoading(false);
        }
    }, [filters, token, userInfo, generalSettings]);

    const handleEditBill = useCallback((row) => {
        console.log(userInfo.Designation);
        const shouldSkipPermission = userInfo?.Designation === 'Admin';

        if (shouldSkipPermission || hasPermission('Pharmacy Bill Edit', 'Create')) {

            // Set invoice number in localStorage for SalesEntry to pick up
            localStorage.setItem('editInvoiceNo', row.InvoiceNo.toString());
            if (row.Sequence) {
                localStorage.setItem('editInvoiceSequence', row.Sequence.toString());
            }
            
            // Navigate to SalesEntry page
            if (tabContext && tabContext.openTab) {
                tabContext.openTab('salesentry', 'Sales Entry');
            } else {
                toast.error('Unable to open Sales Entry page', { autoClose: 2000 });
            }

        } else {
            Swal.fire({
                title: 'Access Denied',
                text: "You don't have permission to access this module.",
                icon: 'error',
                confirmButtonColor: '#3085d6',
            });
        }

    }, [tabContext, userInfo, permissions]);

    const cancelSalesEntry = useCallback(
        async (row) => {
            setLoading(true);
            try {
                const response = await axios.post(
                    `${BASE_URL}${CANCEL_SALES_ENTRY}`,
                    {},
                    {
                        headers: {
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            Authorization: `Bearer ${token}`,
                            Sequence: row.Sequence || 0,
                            InvoiceNo: row.InvoiceNo.toString(),
                            EditNo: row.EditNo || 0,
                        },
                    }
                );

                if (response.status === 200) {
                    toast.success('Sales entry cancelled successfully', { autoClose: 2000 });
                    await getSalesReport();
                } else {
                    toast.error('Failed to cancel sales entry', { autoClose: 2000 });
                }
            } catch (error) {
                console.error('Error cancelling sales entry:', error);
                const errorMessage =
                    error.response?.data?.message || error.message || 'Failed to cancel sales entry';
                toast.error(errorMessage, { autoClose: 2000 });
            } finally {
                setLoading(false);
            }
        },
        [token, getSalesReport]
    );

    const submitCancelApprovalRequest = useCallback(
        async (row, reason) => {
            setLoading(true);
            try {
                const response = await axios.post(
                    `${BASE_URL}${POST_APPROVAL_ENTRY}`,
                    {
                        entryName: 'PharmacyBill',
                        eventType: 'Cancel',
                        sequence: row.Sequence ?? null,
                        invoiceNo: row.InvoiceNo ?? null,
                        editNo: row.EditNo ?? null,
                        addedUserName: userInfo?.UserName ?? null,
                        reason: reason ?? null,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Accept: '*/*',
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (response.status === 200) {
                    toast.success(response?.data?.message || 'Cancel approval request submitted.', {
                        autoClose: 1000,
                        icon: <FiCheckCircle className="text-green-500" />,
                    });
                    await cancelSalesEntry(row);
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Could not submit approval request', {
                    autoClose: 1000,
                    icon: <FiAlertCircle className="text-red-500" />,
                });
            } finally {
                setLoading(false);
            }
        },
        [token, userInfo, cancelSalesEntry]
    );

    const handleDeleteBill = useCallback(
        async (row) => {
            console.log(userInfo.Designation);

            const shouldSkipPermission = userInfo?.Designation === 'Admin';

            if (shouldSkipPermission || hasPermission('Pharmacy Bill Cancel', 'Create')) {
                const trimmedName = row.PatientName && String(row.PatientName).trim();
                const approvalNeededPharmacyBillCancel = Boolean(
                    generalSettings?.approvalNeededPharmacyBillCancel
                );

                const swalOptions = approvalNeededPharmacyBillCancel
                    ? {
                          title: 'Send approval to admin?',
                          text: trimmedName
                              ? `Send an approval request to cancel the pharmacy bill for ${trimmedName}?`
                              : 'Send an approval request to the admin to cancel this pharmacy bill?',
                          icon: 'question',
                          confirmButtonText: 'Yes, send request',
                      }
                    : {
                          title: 'Cancel/Delete Invoice?',
                          text: `Are you sure you want to cancel/delete Invoice No: ${row.InvoiceNo}? This action cannot be undone.`,
                          icon: 'warning',
                          confirmButtonText: 'Yes, cancel it',
                      };

                const result = await Swal.fire({
                    ...swalOptions,
                    showCancelButton: true,
                    confirmButtonColor: approvalNeededPharmacyBillCancel ? '#4f46e5' : '#ef4444',
                    cancelButtonColor: '#d1d5db',
                    cancelButtonText: approvalNeededPharmacyBillCancel ? 'Cancel' : 'No, keep it',
                    customClass: {
                        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5',
                        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 mr-2',
                        popup: 'rounded-xl shadow-xl',
                    },
                });

                if (!result.isConfirmed) {
                    return;
                }

                if (approvalNeededPharmacyBillCancel) {
                    const reasonResult = await Swal.fire({
                        title: 'Cancellation Reason',
                        html: `
                          <div class="text-left">
                            <label for="swal-cancel-reason" class="block text-sm font-medium text-gray-700 mb-2">
                              Please provide a reason for this cancel approval request:
                            </label>
                            <textarea
                              id="swal-cancel-reason"
                              class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                              rows="4"
                              placeholder="Enter reason for cancellation..."
                            ></textarea>
                          </div>
                        `,
                        showCancelButton: true,
                        confirmButtonColor: '#4f46e5',
                        cancelButtonColor: '#d1d5db',
                        confirmButtonText: 'Submit Request',
                        cancelButtonText: 'Cancel',
                        customClass: {
                            confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5',
                            cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 mr-2',
                            popup: 'rounded-xl shadow-xl',
                        },
                        preConfirm: () => {
                            const reason = document.getElementById('swal-cancel-reason')?.value?.trim();
                            if (!reason) {
                                Swal.showValidationMessage('Please provide a reason for cancellation');
                                return false;
                            }
                            return reason;
                        },
                        allowOutsideClick: () => !Swal.isLoading(),
                    });

                    if (reasonResult.isConfirmed) {
                        await submitCancelApprovalRequest(row, reasonResult.value);
                    }
                    return;
                }

                await cancelSalesEntry(row);
            } else {
                Swal.fire({
                    title: 'Access Denied',
                    text: "You don't have permission to access this module.",
                    icon: 'error',
                    confirmButtonColor: '#3085d6',
                });
            }
        },
        [token, userInfo, permissions, generalSettings, submitCancelApprovalRequest, cancelSalesEntry]
    );

    // Filter sales data based on search term and payment status
    const filteredSalesData = useMemo(() => {
        let filtered = salesData;

        // Filter by search term (mode: patient+invoice or doctor only)
        if (filters.search && filters.search.trim() !== '') {
            const searchTerm = filters.search.toLowerCase().trim();
            const searchMode = filters.searchMode || 'patientInvoice';

            if (searchMode === 'doctor') {
                filtered = filtered.filter((row) => {
                    const doctorName = (row.DoctorName || '').toLowerCase();
                    return doctorName.includes(searchTerm);
                });
            } else {
                filtered = filtered.filter((row) => {
                    const patientName = (row.PatientName || '').toLowerCase();
                    const invoiceNo = (row.InvoiceNo || '').toString().toLowerCase();
                    return patientName.includes(searchTerm) || invoiceNo.includes(searchTerm);
                });
            }
        }

        // Filter by payment status
        if (filters.paymentStatus && filters.paymentStatus !== 'all') {
            filtered = filtered.filter((row) => {
                const balanceDue = parseFloat(row.BalanceDue || 0);
                if (filters.paymentStatus === 'pending') {
                    return balanceDue > 0;
                } else if (filters.paymentStatus === 'completed') {
                    return balanceDue === 0;
                }
                return true;
            });
        }

        return filtered;
    }, [salesData, filters.search, filters.searchMode, filters.paymentStatus]);

    // Pagination calculations
    const totalPages = Math.ceil(filteredSalesData.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedData = filteredSalesData.slice(startIndex, endIndex);

    const handleSort = (key) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
        }));
    };

    const sortedPaginatedData = useMemo(() => {
        if (!sortConfig.key) return paginatedData;
        const sorted = [...paginatedData];
        sorted.sort((a, b) => {
            const key = sortConfig.key;
            let aVal = a[key];
            let bVal = b[key];

            if (key === 'GrandTotal') {
                aVal = parseFloat(a.TotalAmount || 0) + parseFloat(a.DiscountAmount || 0);
                bVal = parseFloat(b.TotalAmount || 0) + parseFloat(b.DiscountAmount || 0);
            } else if (['TaxAmount', 'DiscountAmount', 'TotalAmount', 'BalanceDue', 'InvoiceNo'].includes(key)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            } else if (key === 'CreatedDate' || key === 'EntryDate') {
                aVal = new Date(a.CreatedDate || a.EntryDate || 0).getTime();
                bVal = new Date(b.CreatedDate || b.EntryDate || 0).getTime();
            } else {
                aVal = String(aVal ?? '').toLowerCase();
                bVal = String(bVal ?? '').toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [paginatedData, sortConfig]);

    const stickyThClass = 'sticky top-0 z-20 bg-gray-200';

    const SortableHeader = ({ sortKey, label, className = 'text-left' }) => (
        <th
            className={`px-4 py-3 ${className} text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap cursor-pointer hover:bg-slate-300/60 transition-colors ${stickyThClass}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className={`flex items-center ${className.includes('right') ? 'justify-end' : ''}`}>
                {label}
                {sortConfig.key === sortKey && (
                    <span className="ml-1">{sortConfig.direction === 'ascending' ? '↑' : '↓'}</span>
                )}
            </div>
        </th>
    );

    const renderInvoiceItemsTable = (details, invoiceKey) => {
        if (loadingDetails.has(invoiceKey)) {
            return (
                <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600 mb-3"></div>
                    <p className="text-sm text-slate-500">Loading invoice details...</p>
                </div>
            );
        }

        if (!details?.details?.length) {
            return (
                <div className="text-center py-6">
                    <p className="text-sm text-slate-500">No invoice details available</p>
                </div>
            );
        }

        return (
            <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-max w-full divide-y divide-slate-200 text-xs text-slate-700">
                    <thead className="bg-slate-200 sticky top-0 z-10">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">SI</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Item Name</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-700 whitespace-nowrap">Batch</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Qty</th>
                            {canShowPhReports && (
                                <>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">MRP</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Sales Price</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Sales Rate</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Total</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Disc %</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Disc Amt</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Tax</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Tax Code</th>
                                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">Net Total</th>
                                    {userInfo?.Designation === 'Admin' && (
                                        <th className="px-3 py-2 text-right text-xs font-semibold text-green-700 bg-green-50 whitespace-nowrap">Profit</th>
                                    )}
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-200">
                        {details.details.map((detail, detailIndex) => (
                            <tr key={detailIndex} className="hover:bg-indigo-50/30">
                                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{detailIndex + 1}</td>
                                <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{detail.ItemName || '-'}</td>
                                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{detail.BatchCode || '-'}</td>
                                <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{parseFloat(detail.Qty || 0).toFixed(2)}</td>
                                {canShowPhReports && (
                                    <>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.MRP || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.SalesPrice || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">
                                            {(() => {
                                                const mrp = parseFloat(detail.MRP || 0);
                                                const taxCode = parseFloat(detail.TaxCode || 0);
                                                const salesRate = mrp - (mrp * (taxCode / 100));
                                                return `₹${salesRate.toFixed(2)}`;
                                            })()}
                                        </td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.Total || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{parseFloat(detail.Disc_Percentage || 0).toFixed(2)}%</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.Disc_Amount || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">₹{parseFloat(detail.TaxAmount || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700 whitespace-nowrap">{detail.TaxCode || '-'}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-slate-900 whitespace-nowrap">₹{(parseFloat(detail.NetTotal || 0) + parseFloat(detail.TaxAmount || 0)).toFixed(2)}</td>
                                        {userInfo?.Designation === 'Admin' && (
                                            <td className="px-3 py-2 text-right font-semibold text-green-700 bg-green-50/50 whitespace-nowrap">₹{parseFloat(detail.GP_Amount || 0).toFixed(2)}</td>
                                        )}
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {canShowPhReports && userInfo?.Designation === 'Admin' && details.header && (
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200 text-sm">
                        <span className="text-green-700 font-medium">Total Profit %: </span>
                        <span className="font-bold text-green-800 mr-4">{parseFloat(details.header.GP_Percentage || 0).toFixed(2)}%</span>
                        <span className="text-green-700 font-medium">Total Profit: </span>
                        <span className="font-bold text-green-800">₹{parseFloat(details.header.GPAmount || 0).toFixed(2)}</span>
                    </div>
                )}
            </div>
        );
    };

    const TABLE_COL_COUNT = canShowPhReports ? 14 : 8;

    const tableColGroup = canShowPhReports ? (
        <colgroup>
            <col className="w-10" />
            <col className="w-14" />
            <col className="min-w-[100px]" />
            <col className="min-w-[160px]" />
            <col className="min-w-[160px]" />
            <col className="min-w-[140px]" />
            <col className="min-w-[120px]" />
            <col className="min-w-[90px]" />
            <col className="min-w-[90px]" />
            <col className="min-w-[90px]" />
            <col className="min-w-[100px]" />
            <col className="min-w-[90px]" />
            <col className="min-w-[100px]" />
            <col className="min-w-[90px]" />
        </colgroup>
    ) : (
        <colgroup>
            <col className="w-10" />
            <col className="w-14" />
            <col className="min-w-[100px]" />
            <col className="min-w-[160px]" />
            <col className="min-w-[160px]" />
            <col className="min-w-[140px]" />
            <col className="min-w-[120px]" />
            <col className="min-w-[90px]" />
        </colgroup>
    );

    // Reset to page 1 when filters change or new data is loaded
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.search, filters.searchMode, filters.paymentStatus, salesData.length]);

    // Toggle bill expansion and fetch details on demand
    const toggleBillExpansion = useCallback(async (row) => {
        const invoiceKey = `${row.Sequence}_${row.InvoiceNo}`;
        const isExpanded = expandedBills.has(invoiceKey);

        if (isExpanded) {
            // Collapse: just remove from expanded set
            setExpandedBills(prev => {
                const newSet = new Set(prev);
                newSet.delete(invoiceKey);
                return newSet;
            });
        } else {
            // Expand: add to expanded set and fetch details if not already loaded
            setExpandedBills(prev => new Set(prev).add(invoiceKey));

            // Only fetch if details are not already loaded
            if (!invoiceDetails[invoiceKey]) {
                setLoadingDetails(prev => new Set(prev).add(invoiceKey));
                try {
                    const response = await axios.get(`${BASE_URL}${GET_INVOICE_DETAILS}`, {
                        headers: {
                            'XApiKey': process.env.REACT_APP_API_KEY,
                            'Authorization': `Bearer ${token}`,
                        },
                        params: {
                            Sequence: row.Sequence,
                            InvoiceNo: row.InvoiceNo.toString(),
                        }
                    });

                    if (response.status === 200) {
                        const invoiceData = response.data.data;
                        if (invoiceData && invoiceData.details) {
                            setInvoiceDetails(prev => ({
                                ...prev,
                                [invoiceKey]: invoiceData
                            }));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching invoice details:', error);
                    toast.error('Failed to load invoice details', { autoClose: 2000 });
                } finally {
                    setLoadingDetails(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(invoiceKey);
                        return newSet;
                    });
                }
            }
        }
    }, [expandedBills, invoiceDetails, token]);


    return (
        <div className="flex h-full bg-background">
            {/* <LeftMenu /> */}
            <ToastContainer
                position="top-center"
                autoClose={1000}
                limit={1}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Slide}
            />
            
            {loading && <Spinner />}

            <ExportFormatModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onSelectFormat={handleExportFormat}
            />

            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 mb-2">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div className="min-w-0 flex-1">
                            {!embedded && (
                                <div className="mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500">
                                        Sales Reports
                                    </p>
                                    <h1 className="text-lg font-bold text-slate-800">
                                        Sales Bill Wise
                                    </h1>
                                </div>
                            )}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-3">
                                {reportConfig.filters.map((filter) => {
                                    if (filter.type === 'date') {
                                        return (
                                            <label key={filter.id} className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[9.5rem]">
                                                {filter.label}
                                                <input
                                                    type="date"
                                                    value={filters[filter.id] ?? ''}
                                                    min={filter.id === 'toDate' ? filters.fromDate : undefined}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                                                />
                                            </label>
                                        );
                                    }

                                    if (filter.type === 'select') {
                                        return (
                                            <RestrictedBlur
                                                key={filter.id}
                                                restricted={!canShowPhReports}
                                                className="flex flex-col gap-1 text-xs font-medium text-slate-700 shrink-0 w-[11rem]"
                                            >
                                                <span>{filter.label}</span>
                                                <select
                                                    value={filters[filter.id] ?? ''}
                                                    onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                    disabled={!canShowPhReports}
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition disabled:cursor-not-allowed"
                                                >
                                                    {(filter.options ?? []).map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </RestrictedBlur>
                                        );
                                    }

                                    if (filter.type === 'search') {
                                        const searchMode = filters.searchMode || 'patientInvoice';
                                        return (
                                            <RestrictedBlur
                                                key={filter.id}
                                                restricted={!canShowPhReports}
                                                className="flex flex-col gap-1 text-xs font-medium text-slate-700 flex-1 min-w-0 w-full"
                                            >
                                                <div className="flex items-center justify-between gap-2">
                                                    <span>{filter.label}</span>
                                                    <div
                                                        className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5"
                                                        role="group"
                                                        aria-label="Search type"
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() => canShowPhReports && handleFilterChange('searchMode', 'patientInvoice')}
                                                            disabled={!canShowPhReports}
                                                            className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                                                                searchMode === 'patientInvoice'
                                                                    ? 'bg-indigo-600 text-white shadow-sm'
                                                                    : 'text-slate-600 hover:text-indigo-600'
                                                            }`}
                                                        >
                                                            Patient / Inv
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => canShowPhReports && handleFilterChange('searchMode', 'doctor')}
                                                            disabled={!canShowPhReports}
                                                            className={`rounded px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                                                                searchMode === 'doctor'
                                                                    ? 'bg-indigo-600 text-white shadow-sm'
                                                                    : 'text-slate-600 hover:text-indigo-600'
                                                            }`}
                                                        >
                                                            Doctor
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="relative">
                                                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                                                    <input
                                                        type="text"
                                                        value={filters[filter.id] ?? ''}
                                                        onChange={(event) => handleFilterChange(filter.id, event.target.value)}
                                                        disabled={!canShowPhReports}
                                                        placeholder={
                                                            searchMode === 'doctor'
                                                                ? 'Search by doctor name...'
                                                                : 'Search by patient name or invoice no...'
                                                        }
                                                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition disabled:cursor-not-allowed"
                                                    />
                                                </div>
                                            </RestrictedBlur>
                                        );
                                    }

                                    return null;
                                })}
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-start lg:justify-end gap-2 sm:gap-3">
                            <button
                                type="button"
                                onClick={handleRunReport}
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
                            >
                                <FiRefreshCcw size={16} />
                                Generate
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const initial = buildInitialFilters(reportConfig.filters);
                                    if (canShowPhReports) {
                                        setFilters(initial);
                                    } else {
                                        setFilters((prev) => ({
                                            ...prev,
                                            fromDate: initial.fromDate,
                                            toDate: initial.toDate,
                                        }));
                                    }
                                }}
                                className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500"
                            >
                                Reset
                            </button>
                            <RestrictedBlur restricted={!canShowPhReports}>
                                <button
                                    type="button"
                                    onClick={handleExport}
                                    disabled={!canShowPhReports}
                                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-indigo-300 hover:text-indigo-500 disabled:cursor-not-allowed"
                                >
                                    <FiDownloadCloud size={16} />
                                    Export
                                </button>
                            </RestrictedBlur>
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-hidden flex flex-col">
                    <div className="flex flex-col gap-3 flex-1 min-h-0">
                        <HighlightMetricsModal
                            isOpen={isMetricsModalOpen}
                            onOpen={() => setIsMetricsModalOpen(true)}
                            onClose={() => setIsMetricsModalOpen(false)}
                        >
                            <div className="grid gap-4 md:grid-cols-5">
                                <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                        Total Bills
                                    </p>
                                    <p className="mt-1 text-md font-semibold text-indigo-600">{filteredSalesData.length}</p>
                                </div>
                                {canShowPhReports && (
                                    <>
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                                Total Amount
                                            </p>
                                            <p className="mt-1 text-md font-semibold text-indigo-600">
                                                ₹{filteredSalesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || 0)), 0).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                                Total Discount
                                            </p>
                                            <p className="mt-1 text-md font-semibold text-indigo-600">
                                                ₹{filteredSalesData.reduce((sum, item) => sum + (parseFloat(item.DiscountAmount || 0)), 0).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                                Grand Total
                                            </p>
                                            <p className="mt-1 text-md font-semibold text-indigo-600">
                                                ₹{filteredSalesData.reduce((sum, item) => sum + (parseFloat(item.TotalAmount || 0) + parseFloat(item.DiscountAmount || 0)), 0).toFixed(2)}
                                            </p>
                                        </div>
                                        <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-2 text-slate-700 shadow-sm">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-500">
                                                Total Pending
                                            </p>
                                            <p className="mt-1 text-md font-semibold text-indigo-600">
                                                ₹{filteredSalesData.reduce((sum, item) => sum + (parseFloat(item.BalanceDue || 0)), 0).toFixed(2)}
                                            </p>
                                        </div>
                                    </>
                                )}
                            </div>
                        </HighlightMetricsModal>

                        {/* Table Section */}
                        <section className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0 mb-4">
                                <div className="flex flex-col gap-2">
                                    <p className="text-sm text-slate-500">
                                        Showing <span className="font-semibold text-slate-700">{startIndex + 1}-{Math.min(endIndex, filteredSalesData.length)}</span> of <span className="font-semibold text-slate-700">{filteredSalesData.length}</span> {(filters.search || (filters.paymentStatus && filters.paymentStatus !== 'all')) ? 'filtered' : ''} bills {(filters.search || (filters.paymentStatus && filters.paymentStatus !== 'all')) && `out of ${salesData.length} total`} for this report.
                                        {filters.paymentStatus && filters.paymentStatus !== 'all' && (
                                            <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                                                {filters.paymentStatus === 'pending' ? 'Pending Only' : 'Completed Only'}
                                            </span>
                                        )}
                                    </p>
                                    {/* Items per page selector */}
                                    {/* <div className="flex items-center gap-2">
                                        <label className="text-xs text-slate-600">Items per page:</label>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="rounded border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                                        >
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                            <option value={200}>200</option>
                                        </select>
                                    </div> */}
                                </div>
                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Previous
                                        </button>
                                        <div className="flex items-center gap-1">
                                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (currentPage <= 3) {
                                                    pageNum = i + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = currentPage - 2 + i;
                                                }
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                                                            currentPage === pageNum
                                                                ? 'bg-indigo-600 text-white border-indigo-600'
                                                                : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <span className="text-sm text-slate-600">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </div>
                            {filteredSalesData.length > 0 ? (
                                <div className="mt-2 flex-1 min-h-0 overflow-auto rounded-xl border border-slate-200 bg-white">
                                            <table className="min-w-max divide-y divide-slate-200 text-sm text-slate-700 border-collapse">
                                                {tableColGroup}
                                                <thead className="bg-gray-200">
                                                    <tr>
                                                        <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}></th>
                                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                                                            SI No
                                                        </th>
                                                        <SortableHeader sortKey="InvoiceNo" label="Inv. No" />
                                                        <SortableHeader sortKey="CreatedDate" label="Date" />
                                                        <SortableHeader sortKey="PatientName" label="Patient Name" />
                                                        <SortableHeader sortKey="DoctorName" label="Doctor Name" />
                                                        <SortableHeader sortKey="UserName" label="User Name" />
                                                        {canShowPhReports && (
                                                            <>
                                                                <SortableHeader sortKey="GrandTotal" label="G Total" className="text-right" />
                                                                <SortableHeader sortKey="TaxAmount" label="Tax Amount" className="text-right" />
                                                                <SortableHeader sortKey="DiscountAmount" label="Discount" className="text-right" />
                                                                <SortableHeader sortKey="TotalAmount" label="Total Amount" className="text-right" />
                                                                <SortableHeader sortKey="BalanceDue" label="Balance Due" className="text-right" />
                                                                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                                                                    Payment
                                                                </th>
                                                            </>
                                                        )}
                                                        <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap ${stickyThClass}`}>
                                                            Actions
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 bg-white">
                                                {sortedPaginatedData.map((row, index) => {
                                                    const invoiceKey = `${row.Sequence}_${row.InvoiceNo}`;
                                                    const details = invoiceDetails[invoiceKey];
                                                    const totalAmount = parseFloat(row.TotalAmount || 0);
                                                    const discountAmount = parseFloat(row.DiscountAmount || 0);
                                                    const grandTotal = totalAmount + discountAmount;
                                                    const balanceDue = parseFloat(row.BalanceDue || 0);
                                                    const mobileNo = details?.header?.MobileNo
                                                        ? String(details.header.MobileNo).trim()
                                                        : null;
                                                    const isExpanded = expandedBills.has(invoiceKey);
                                                    const siNo = startIndex + index + 1;

                                                    return (
                                                        <React.Fragment key={`${row.InvoiceNo}_${row.Sequence}_${index}`}>
                                                            <tr className="border-b border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                                                                <td className="px-3 py-3 whitespace-nowrap">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => toggleBillExpansion(row)}
                                                                        className="inline-flex items-center justify-center p-1 text-indigo-600 hover:bg-indigo-100 rounded transition-colors"
                                                                        title={isExpanded ? 'Hide line items' : 'Show line items'}
                                                                    >
                                                                        {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                                                    </button>
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-slate-700 font-medium">
                                                                    {siNo}
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap font-semibold text-indigo-700">
                                                                    {formatInvoiceForPrint(row.InvoiceNo, row.Sequence)}
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                                                                    <div className="flex items-center">
                                                                        {row.CreatedDate
                                                                            ? `${new Date(row.CreatedDate).toLocaleDateString('en-GB')} ${new Date(row.CreatedDate).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
                                                                            : row.EntryDate
                                                                                ? new Date(row.EntryDate).toLocaleDateString('en-GB')
                                                                                : '-'}
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-slate-700 min-w-[140px]">
                                                                    <div className="flex items-center">
                                                                        <div>
                                                                            <div className="font-medium">{row.PatientName || 'N/A'}</div>
                                                                            {mobileNo && (
                                                                                <div className="text-xs text-gray-500">{mobileNo}</div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-slate-700 min-w-[120px]">
                                                                    {row.DoctorName || '-'}
                                                                </td>
                                                                <td className="px-4 py-3 whitespace-nowrap text-slate-700">
                                                                    {row.UserName || 'N/A'}
                                                                </td>
                                                                {canShowPhReports && (
                                                                    <>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-slate-700">
                                                                            ₹{grandTotal.toFixed(2)}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-slate-700">
                                                                            ₹{parseFloat(row.TaxAmount || 0).toFixed(2)}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-right text-green-600 font-medium">
                                                                            ₹{discountAmount.toFixed(2)}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap text-right font-semibold text-indigo-600">
                                                                            ₹{totalAmount.toFixed(2)}
                                                                        </td>
                                                                        <td className={`px-4 py-3 whitespace-nowrap text-right font-medium ${balanceDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                                                            ₹{balanceDue.toFixed(2)}
                                                                        </td>
                                                                        <td className="px-4 py-3 whitespace-nowrap">
                                                                            {balanceDue > 0 ? (
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                                    <FiAlertCircle size={14} className="mr-1" />
                                                                                    Pending
                                                                                </span>
                                                                            ) : (
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                                                    <FiCheckCircle size={14} className="mr-1" />
                                                                                    Paid
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                    </>
                                                                )}
                                                                <td className="px-4 py-3 whitespace-nowrap">
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleEditBill(row)}
                                                                            className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                                                                            title="Edit Bill"
                                                                        >
                                                                            <FiEdit2 size={16} />
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteBill(row)}
                                                                            className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                                                                            title="Delete/Cancel Bill"
                                                                        >
                                                                            <FiTrash2 size={16} />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {isExpanded && (
                                                                <tr className="bg-slate-50/80">
                                                                    <td colSpan={TABLE_COL_COUNT} className="px-4 py-4">
                                                                        <h4 className="text-sm font-semibold text-slate-700 mb-3">
                                                                            Invoice Items{details?.details?.length ? ` (${details.details.length})` : ''}
                                                                        </h4>
                                                                        {renderInvoiceItemsTable(details, invoiceKey)}
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                                </tbody>
                                            </table>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <FiFileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-500 text-lg">No data available for the selected period</p>
                                </div>
                            )}
                            {/* Bottom Pagination Controls */}
                            {filteredSalesData.length > 0 && totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-200">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                        disabled={currentPage === 1}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Previous
                                    </button>
                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 7) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 4) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 3) {
                                                pageNum = totalPages - 6 + i;
                                            } else {
                                                pageNum = currentPage - 3 + i;
                                            }
                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    className={`px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                                        currentPage === pageNum
                                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                                            : 'border-slate-200 bg-white text-slate-700 hover:bg-indigo-50 hover:border-indigo-300'
                                                    }`}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <span className="text-sm text-slate-600 px-2">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage === totalPages}
                                        className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default withAuth(SalesBillWise);